import { useNavigate, useSearchParams } from 'react-router-dom'
import { useCallback, useState } from 'react'
import Lottie from 'react-lottie-player'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'usehooks-ts'

import { useAuth, useDonationWallet, useWindowDimensions } from '@percent/workplace-giving/common/hooks'
import { formatMoney, type Money } from '@percent/utility'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'
import { getPartnerFromAuthState } from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { Box, Button, ButtonText, Heading, Modal, Text } from '@percent/lemonade'
import confetti from '@percent/workplace-giving/assets/animations/confetti2.json'
import coinIcon from '@percent/workplace-giving/assets/icons/coin.svg'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import classes from './DonationWalletGiftPopup.module.scss'

function Popup({ wallet, onClose }: { wallet: Money; onClose: () => void }) {
  const { t, i18n } = useTranslation()
  const { state } = useAuth()
  const partner = getPartnerFromAuthState(state)
  const { track } = useAnalytics()
  const isDesktop = useMediaQuery('(min-width: 600px)')
  const { width: windowWidth } = useWindowDimensions()
  const formattedMoney = formatMoney(wallet, {
    locale: i18n.language,
    hideDecimalsIfZero: true
  })
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(true)

  const handleDismiss = useCallback(() => {
    onClose()
    setModalOpen(false)
  }, [onClose])

  const handleClose = useCallback(() => {
    handleDismiss()
    track({ event: 'Claim Wallet Funds CTA Clicked' })
    navigate(AppRoute.DONATE)
  }, [handleDismiss, navigate, track])

  return (
    <Modal
      borderRadiiSize="xlarge"
      open={modalOpen}
      blur
      width={isDesktop ? 496 : windowWidth - 48}
      data-testid="donationWalletGiftPopup"
    >
      <Box className={classes.container}>
        <Box className={classes.contentWrapper}>
          <Box className={classes.walletGiftHeader}>
            <div className={classes.walletAmountContainer}>
              <img src={coinIcon} alt="coin icon" />
              <Heading level="h3">{formattedMoney}</Heading>
            </div>
          </Box>
          <Box className={classes.headingWrapper}>
            <Heading level="h2" align="center">
              {t('workplace_giving.onboarding.donation_wallet_gift_popup.title')}
            </Heading>
            <Text size="small" className={classes.description}>
              {t('workplace_giving.onboarding.donation_wallet_gift_popup.description', {
                amount: formattedMoney,
                partnerName: partner?.name
              })}
            </Text>
          </Box>
          <Box className={classes.buttonsWrapper}>
            <Button
              data-testid="claimGiftBtn"
              variant="primary"
              type="button"
              shape="oval"
              stretch
              onClick={handleClose}
            >
              {t('workplace_giving.onboarding.donation_wallet_gift_popup.ctaLabel')}
            </Button>
            <ButtonText data-testid="dismissGiftBtn" onClick={handleDismiss}>
              {t('workplace_giving.onboarding.donation_wallet_gift_popup.dismissBtnLabel')}
            </ButtonText>
          </Box>
        </Box>
      </Box>
      <Lottie data-testid="confettiAnimation" className={classes.confetti} animationData={confetti} loop={false} play />
    </Modal>
  )
}

export function DonationWalletGiftPopup() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: wallet } = useDonationWallet()

  const clearFinishedOnboarding = useCallback(
    () =>
      setSearchParams(prev => {
        const newSearchParams = new URLSearchParams(prev)
        newSearchParams.delete('finished_onboarding')

        return newSearchParams
      }),
    [setSearchParams]
  )

  if (searchParams.get('finished_onboarding') !== 'true' || !wallet?.balance?.amount) {
    return null
  }

  return <Popup wallet={wallet.balance} onClose={clearFinishedOnboarding} />
}
